/**
 * Demo-without-login session state (client side).
 *
 * A "demo" session is a short-lived, read-only JWT minted by
 * `POST /api/v1/public/demo/start` into one of the curated golden demo tenants.
 * The token itself carries the authoritative `demo_read_only` claim and the
 * backend `demo_read_only_guard` middleware blocks every mutating request — this
 * module only tracks the flag client-side so the UI can show a persistent banner,
 * route Luma through the ephemeral one-shot path, and tweak affordances. It is a
 * UI hint, never a security boundary.
 *
 * The flag is set when a demo session starts and cleared on logout / session
 * expiry (alongside the auth tokens).
 */

const DEMO_KEY = 'panluma_demo';

/** Mark the current session as a read-only demo into `variation` (a tenant key). */
export function setDemoMode(variation: string): void {
  try {
    localStorage.setItem(DEMO_KEY, variation);
  } catch {
    /* localStorage unavailable (private mode) — banner just won't show. */
  }
}

/** Clear the demo flag. Call from logout / session-expired alongside token removal. */
export function clearDemoMode(): void {
  try {
    localStorage.removeItem(DEMO_KEY);
  } catch {
    /* noop */
  }
}

/** The active demo variation key, or null when not in a demo session. */
export function getDemoVariation(): string | null {
  try {
    return localStorage.getItem(DEMO_KEY);
  } catch {
    return null;
  }
}

/** True while a read-only demo session is active. */
export function isDemoMode(): boolean {
  return getDemoVariation() !== null;
}
